// Quy tắc nghiệp vụ thẻ liệu trình — hàm thuần, không phụ thuộc store / giao diện (dễ chuyển lên backend)
import dayjs from 'dayjs';
import {
  DEFAULT_ACTIVATION_MONTHS, policyOf, pricingOf, serviceOf,
  type CardLine, type CardPolicy, type CardStatus, type Installment, type TreatmentCard, type TreatmentConfig, type TreatmentPackage, type ValidityBasis,
} from './data';

const sum = (xs: number[]) => xs.reduce((s, x) => s + x, 0);
export const round1000 = (n: number) => Math.round(n / 1000) * 1000;
const floor1000 = (n: number) => Math.floor(n / 1000) * 1000;

export const ymd = (d: Date | string = new Date()) => dayjs(d).format('YYYY-MM-DD');
/** Số ngày từ a tới b (b − a), tính theo ngày lịch */
export const daysBetween = (a: string, b: string) => dayjs(b).startOf('day').diff(dayjs(a).startOf('day'), 'day');
export const addMonthsYmd = (date: string, months: number) => dayjs(date).add(months, 'month').format('YYYY-MM-DD');
export const addDaysYmd = (date: string, days: number) => dayjs(date).add(days, 'day').format('YYYY-MM-DD');

// ---------------------------------------------------------------------------
// Hạn dùng

/** Hạn của thẻ không thời hạn */
export const NO_EXPIRY = '9999-12-31';
export const noExpiry = (expiresAt: string) => expiresAt >= '9999';
const dmy = (d: string) => dayjs(d).format('DD/MM/YYYY');

type ValidityPkg = Pick<TreatmentPackage, 'validityMonths' | 'validityBasis' | 'activationMonths'>;

/**
 * Hạn dùng khi phát hành thẻ. Tính từ buổi đầu: thẻ chờ kích hoạt, expiresAt là hạn phải dùng buổi đầu;
 * buổi đầu được trừ thì hạn dùng = ngày đó + số tháng hạn dùng (xem activationOf)
 */
export function issueValidity(pkg: ValidityPkg, saleDate: string): Pick<TreatmentCard, 'expiresAt' | 'validityBasis' | 'validityMonths'> {
  const basis: ValidityBasis = pkg.validityBasis || 'purchase';
  const expiresAt = basis === 'unlimited' ? NO_EXPIRY
    : basis === 'first_use' ? addMonthsYmd(saleDate, pkg.activationMonths || DEFAULT_ACTIVATION_MONTHS)
      : addMonthsYmd(saleDate, pkg.validityMonths);
  return { expiresAt, validityBasis: basis, validityMonths: pkg.validityMonths };
}

export const awaitingActivation = (c: Pick<TreatmentCard, 'validityBasis' | 'activatedAt'>) => c.validityBasis === 'first_use' && !c.activatedAt;

/** Lần trừ buổi đầu của thẻ chờ kích hoạt: hạn dùng mới tính từ ngày dùng (null nếu thẻ không cần kích hoạt) */
export function activationOf(c: Pick<TreatmentCard, 'validityBasis' | 'activatedAt' | 'validityMonths'>, at: string): { activatedAt: string; expiresAt: string } | null {
  if (!awaitingActivation(c)) return null;
  return { activatedAt: at, expiresAt: addMonthsYmd(ymd(at), c.validityMonths || 0) };
}

/** Hạn dùng của thẻ để hiển thị */
export const expiryText = (c: Pick<TreatmentCard, 'expiresAt' | 'validityBasis' | 'activatedAt'>) => (
  noExpiry(c.expiresAt) ? 'Không thời hạn' : awaitingActivation(c) ? `Chờ kích hoạt – dùng buổi đầu trước ${dmy(c.expiresAt)}` : dmy(c.expiresAt));

/** Quy định hạn dùng của gói, VD "12 tháng từ ngày mua" */
export function validityText(p: ValidityPkg): string {
  const basis = p.validityBasis || 'purchase';
  if (basis === 'unlimited') return 'Không thời hạn';
  if (basis === 'first_use') return `${p.validityMonths} tháng từ buổi dùng đầu (kích hoạt trong ${p.activationMonths || DEFAULT_ACTIVATION_MONTHS} tháng)`;
  return `${p.validityMonths} tháng từ ngày mua`;
}

// ---------------------------------------------------------------------------
// Dựng thẻ từ gói

/** Phân bổ giá bán cho dịch vụ chính theo giá lẻ × số buổi; dịch vụ tặng có giá trị 0 */
export function buildLines(pkg: Pick<TreatmentPackage, 'items'>, price: number): CardLine[] {
  const mains = pkg.items.filter(i => i.role === 'main' && i.sessions > 0);
  const weightOf = (i: { serviceId: string; sessions: number }) => (serviceOf(i.serviceId)?.price || 0) * i.sessions;
  let totalWeight = sum(mains.map(weightOf));
  const useSessions = totalWeight <= 0;
  if (useSessions) totalWeight = sum(mains.map(i => i.sessions));
  return pkg.items.map(i => {
    const svc = serviceOf(i.serviceId);
    const share = i.role === 'main' && i.sessions > 0 && totalWeight > 0 ? (useSessions ? i.sessions : weightOf(i)) / totalWeight : 0;
    return {
      serviceId: i.serviceId, name: svc?.name || i.serviceId, group: svc?.group || '', role: i.role,
      total: i.sessions, bonus: 0, usedBefore: 0, unitValue: share ? (price * share) / i.sessions : 0,
    };
  });
}

/** Lịch thanh toán: đợt 1 = số tiền thu ngay, phần còn lại chia đều (làm tròn nghìn, đợt cuối nhận phần lẻ) */
export function buildPlan(total: number, count: number, first: number, startDate: string, intervalDays: number): Installment[] {
  const n = Math.max(1, Math.floor(count));
  if (n === 1) return [{ no: 1, dueDate: startDate, amount: total }];
  const firstAmt = Math.min(Math.max(0, first), total);
  const rest = total - firstAmt;
  const each = round1000(rest / (n - 1));
  const plan: Installment[] = [{ no: 1, dueDate: startDate, amount: firstAmt }];
  let left = rest;
  for (let i = 2; i <= n; i++) {
    const amount = i === n ? left : Math.min(each, left);
    left -= amount;
    plan.push({ no: i, dueDate: addDaysYmd(startDate, intervalDays * (i - 1)), amount });
  }
  return plan;
}

// ---------------------------------------------------------------------------
// Số dư

const activeUsages = (c: TreatmentCard) => c.usages.filter(u => !u.cancelled);
const liveRefunds = (c: TreatmentCard) => c.refunds.filter(r => r.status !== 'rejected');

export interface LineBalance extends CardLine {
  used: number;
  refunded: number;
  /** Còn dùng được */
  available: number;
  /** Đang có lịch hẹn chưa đến */
  booked: number;
}

export function lineBalances(c: TreatmentCard): LineBalance[] {
  return c.lines.map(l => {
    const used = l.usedBefore + sum(activeUsages(c).flatMap(u => u.items.filter(i => i.serviceId === l.serviceId).map(i => i.qty)));
    const refunded = sum(liveRefunds(c).flatMap(r => r.sessions.filter(s => s.serviceId === l.serviceId).map(s => s.qty)));
    const booked = c.bookings.filter(b => b.status === 'booked' && b.serviceId === l.serviceId).length;
    return { ...l, used, refunded, booked, available: l.total + l.bonus - used - refunded };
  });
}

export interface CardBalance {
  totalSessions: number;
  usedSessions: number;
  remainingSessions: number;
  faceValue: number;
  usedValue: number;
  remainingValue: number;
  /** Tỷ lệ còn lại 0..1 */
  pctLeft: number;
}

export function cardBalance(c: TreatmentCard): CardBalance {
  if (c.kind === 'value') {
    const face = c.faceValue + c.valueAdjust - sum(liveRefunds(c).map(r => r.valueReturned));
    const usedValue = c.valueUsedBefore + sum(activeUsages(c).flatMap(u => u.items.map(i => i.value)));
    const remainingValue = Math.max(0, face - usedValue);
    return { totalSessions: 0, usedSessions: 0, remainingSessions: 0, faceValue: face, usedValue, remainingValue, pctLeft: face > 0 ? remainingValue / face : 0 };
  }
  const lines = lineBalances(c);
  const totalSessions = sum(lines.map(l => l.total + l.bonus - l.refunded));
  const usedSessions = sum(lines.map(l => l.used));
  const remainingSessions = Math.max(0, sum(lines.map(l => l.available)));
  return { totalSessions, usedSessions, remainingSessions, faceValue: 0, usedValue: 0, remainingValue: 0, pctLeft: totalSessions > 0 ? remainingSessions / totalSessions : 0 };
}

/** Giá trị tiền khách đã sử dụng (để tính hoàn tiền và giới hạn theo số đã đóng) */
export function consumedAmount(c: TreatmentCard): number {
  if (c.kind === 'value') {
    if (c.faceValue <= 0) return 0;
    const usedValue = c.valueUsedBefore + sum(activeUsages(c).flatMap(u => u.items.map(i => i.value)));
    return Math.min(usedValue, c.faceValue) * (c.price / c.faceValue);
  }
  // Buổi tặng / điều chỉnh dùng sau cùng nên chỉ tính phần trong số buổi đã mua
  return sum(lineBalances(c).map(l => Math.min(l.used, l.total) * l.unitValue));
}

export const paidAmount = (c: TreatmentCard) => sum(c.payments.map(p => p.amount));
export const refundedAmount = (c: TreatmentCard) => sum(c.refunds.filter(r => r.status === 'paid').map(r => r.amount));
export const outstanding = (c: TreatmentCard) => (c.status === 'active' ? Math.max(0, c.price - paidAmount(c)) : 0);

export function cardStatus(c: TreatmentCard, now = new Date()): CardStatus {
  if (c.status !== 'active') return c.status;
  const b = cardBalance(c);
  if ((c.kind === 'session' ? b.remainingSessions : b.remainingValue) <= 0) return 'completed';
  if (ymd(now) > c.expiresAt) return 'expired';
  return 'active';
}

// ---------------------------------------------------------------------------
// Thanh toán theo đợt

export type InstallmentState = 'paid' | 'overdue' | 'due_soon' | 'upcoming' | 'void';
export interface InstallmentRow extends Installment { paid: number; left: number; state: InstallmentState; overdueDays: number; daysLeft: number }

/** Phân bổ tiền đã thu vào các đợt theo thứ tự (đợt sớm trả trước) */
export function installmentRows(c: TreatmentCard, cfg: Pick<TreatmentConfig, 'dueSoonDays'>, now = new Date()): InstallmentRow[] {
  let pool = paidAmount(c);
  const today = ymd(now);
  return [...c.installments].sort((a, b) => a.no - b.no).map(i => {
    const paid = Math.min(i.amount, Math.max(0, pool));
    pool -= paid;
    const left = i.amount - paid;
    const daysLeft = daysBetween(today, i.dueDate);
    let state: InstallmentState = 'upcoming';
    if (left <= 0) state = 'paid';
    else if (c.status !== 'active') state = 'void';
    else if (daysLeft < 0) state = 'overdue';
    else if (daysLeft <= cfg.dueSoonDays) state = 'due_soon';
    return { ...i, paid, left, state, daysLeft, overdueDays: state === 'overdue' ? -daysLeft : 0 };
  });
}

// ---------------------------------------------------------------------------
// Cảnh báo

export interface CardFlags {
  status: CardStatus;
  low: boolean;
  expiring: boolean;
  daysToExpire: number;
  overdue: boolean;
  overdueAmount: number;
  maxOverdueDays: number;
  dueSoon: boolean;
  debt: number;
  pendingRefund: boolean;
}

export function cardFlags(c: TreatmentCard, cfg: TreatmentConfig, now = new Date()): CardFlags {
  const status = cardStatus(c, now);
  const b = cardBalance(c);
  const rows = installmentRows(c, cfg, now);
  const overdueRows = rows.filter(r => r.state === 'overdue');
  const daysToExpire = daysBetween(ymd(now), c.expiresAt);
  const low = status === 'active' && (c.kind === 'session'
    ? b.remainingSessions <= cfg.lowSessions
    : b.faceValue > 0 && b.remainingValue / b.faceValue * 100 <= cfg.lowValuePct);
  return {
    status,
    low,
    expiring: status === 'active' && daysToExpire <= cfg.expiringDays,
    daysToExpire,
    overdue: overdueRows.length > 0,
    overdueAmount: sum(overdueRows.map(r => r.left)),
    maxOverdueDays: Math.max(0, ...overdueRows.map(r => r.overdueDays)),
    dueSoon: rows.some(r => r.state === 'due_soon'),
    debt: outstanding(c),
    pendingRefund: c.refunds.some(r => r.status === 'pending'),
  };
}

// ---------------------------------------------------------------------------
// Trừ buổi

export interface UsageRequestItem { serviceId: string; qty: number }

/** Giá trị trừ cho 1 lượt: thẻ buổi = số buổi × giá trị phân bổ; thẻ giá trị = giá dịch vụ × số lần */
export function usageItems(c: TreatmentCard, req: UsageRequestItem[]) {
  return req.filter(r => r.qty > 0).map(r => {
    const svc = serviceOf(r.serviceId);
    const line = c.lines.find(l => l.serviceId === r.serviceId);
    const value = c.kind === 'value' ? (svc?.price || 0) * r.qty : Math.round((line?.unitValue || 0) * r.qty);
    return { serviceId: r.serviceId, name: line?.name || svc?.name || r.serviceId, qty: r.qty, value };
  });
}

export interface UsageCheck {
  /** Lỗi chặn cứng */
  errors: string[];
  /** Vướng quy tắc — quản lý duyệt thì được làm */
  holds: string[];
  value: number;
}

export function checkUsage(c: TreatmentCard, req: UsageRequestItem[], cfg: TreatmentConfig, now = new Date()): UsageCheck {
  const errors: string[] = [];
  const holds: string[] = [];
  const items = usageItems(c, req);
  const value = sum(items.map(i => i.value));
  const status = cardStatus(c, now);
  const fmt = (n: number) => new Intl.NumberFormat('vi-VN').format(Math.round(n));

  if (status === 'refunded' || status === 'cancelled') errors.push(status === 'refunded' ? 'Thẻ đã hoàn tiền' : 'Thẻ đã hủy');
  else if (status === 'completed') errors.push('Thẻ đã dùng hết');
  else if (status === 'expired') errors.push(`Thẻ hết hạn từ ${dayjs(c.expiresAt).format('DD/MM/YYYY')} – cần gia hạn trước khi dùng`);
  if (c.refunds.some(r => r.status === 'pending' && r.mode === 'full')) errors.push('Thẻ đang chờ hoàn tiền toàn bộ');
  if (!items.length) errors.push('Chọn ít nhất 1 dịch vụ');

  if (c.kind === 'session') {
    const lines = lineBalances(c);
    items.forEach(i => {
      const l = lines.find(x => x.serviceId === i.serviceId);
      if (!l) errors.push(`${i.name} không có trong thẻ`);
      else if (i.qty > l.available) errors.push(`${l.name} chỉ còn ${Math.max(0, l.available)} buổi`);
    });
  } else {
    const b = cardBalance(c);
    items.forEach(i => {
      if (c.allowedServiceIds.length && !c.allowedServiceIds.includes(i.serviceId)) errors.push(`${i.name} không áp dụng cho thẻ này`);
    });
    if (value > b.remainingValue) errors.push(`Số dư thẻ còn ${fmt(b.remainingValue)}đ, không đủ trừ ${fmt(value)}đ`);
  }

  if (c.status === 'active') {
    const rows = installmentRows(c, cfg, now).filter(r => r.state === 'overdue');
    if (cfg.blockOverdue && rows.length) {
      holds.push(`Còn ${fmt(sum(rows.map(r => r.left)))}đ quá hạn thanh toán (đợt ${rows.map(r => r.no).join(', ')}, trễ ${Math.max(...rows.map(r => r.overdueDays))} ngày)`);
    }
    if (cfg.limitByPaid && items.length) {
      const after: TreatmentCard = { ...c, usages: [...c.usages, { id: '_', at: now.toISOString(), branchId: '', staffId: '', items, by: '', note: '', signed: true }] };
      const consumed = consumedAmount(after);
      const paid = paidAmount(c);
      if (consumed > paid + 1000) holds.push(`Giá trị đã dùng sau lượt này (${fmt(consumed)}đ) vượt số tiền khách đã đóng (${fmt(paid)}đ)`);
    }
  }
  return { errors, holds, value };
}

export const canCancelUsage = (at: string, cfg: Pick<TreatmentConfig, 'cancelUsageHours'>, now = new Date()) =>
  now.getTime() - new Date(at).getTime() <= cfg.cancelUsageHours * 3600000;

// ---------------------------------------------------------------------------
// Hoàn tiền

export interface RefundQuote {
  paid: number;
  consumed: number;
  /** Đã hoàn / đang chờ hoàn (gồm phí) */
  already: number;
  /** Tối đa còn có thể hoàn */
  cap: number;
  gross: number;
  fee: number;
  amount: number;
}

/**
 * Số tiền hoàn = min(giá trị phần trả lại, tiền đã đóng − giá trị đã dùng − đã hoàn) − phí.
 * Dịch vụ tặng không được tính tiền; hoàn toàn bộ thì lấy hết phần còn lại.
 */
export function refundQuote(
  c: TreatmentCard,
  input: { mode: 'partial' | 'full'; sessions?: UsageRequestItem[]; valueReturned?: number; feePct: number },
): RefundQuote {
  const paid = paidAmount(c);
  const consumed = consumedAmount(c);
  const already = sum(liveRefunds(c).map(r => r.amount + r.fee));
  const cap = Math.max(0, paid - consumed - already);
  let gross = cap;
  if (input.mode === 'partial') {
    gross = c.kind === 'session'
      ? sum((input.sessions || []).map(s => (c.lines.find(l => l.serviceId === s.serviceId)?.unitValue || 0) * s.qty))
      : (input.valueReturned || 0) * (c.faceValue > 0 ? c.price / c.faceValue : 0);
    gross = Math.min(gross, cap);
  }
  gross = Math.round(gross);
  const amount = refundAfterFee(gross, input.feePct);
  return { paid, consumed, already, cap, gross, fee: gross - amount, amount };
}

/** Số tiền khách nhận khi hoàn phần trị giá gross: giữ lại feePct%, làm tròn xuống nghìn */
export const refundAfterFee = (gross: number, feePct: number) => Math.max(0, floor1000(gross - gross * Math.max(0, feePct) / 100));

// ---------------------------------------------------------------------------
// Thống kê

export interface MonthUsage { month: string; label: string; visits: number; sessions: number; value: number }

export function monthlyUsage(cards: TreatmentCard[], months: number, now = new Date()): MonthUsage[] {
  const start = dayjs(now).startOf('month').subtract(months - 1, 'month');
  const rows: MonthUsage[] = Array.from({ length: months }, (_, i) => {
    const m = start.add(i, 'month');
    return { month: m.format('YYYY-MM'), label: m.format('MM/YY'), visits: 0, sessions: 0, value: 0 };
  });
  const idx = new Map(rows.map((r, i) => [r.month, i]));
  cards.forEach(c => activeUsages(c).forEach(u => {
    const i = idx.get(dayjs(u.at).format('YYYY-MM'));
    if (i === undefined) return;
    rows[i].visits += 1;
    rows[i].sessions += c.kind === 'session' ? sum(u.items.map(x => x.qty)) : 0;
    rows[i].value += sum(u.items.map(x => x.value));
  }));
  return rows;
}

export const AGING_BUCKETS = [
  { key: 'b30', label: '1–30 ngày', max: 30 },
  { key: 'b60', label: '31–60 ngày', max: 60 },
  { key: 'b90', label: '61–90 ngày', max: 90 },
  { key: 'b90p', label: 'Trên 90 ngày', max: Infinity },
] as const;

/** Tuổi nợ: cộng phần còn thiếu của các đợt quá hạn theo số ngày trễ */
export function agingBuckets(rows: Pick<InstallmentRow, 'state' | 'left' | 'overdueDays'>[]) {
  const out = AGING_BUCKETS.map(b => ({ ...b, amount: 0, count: 0 }));
  rows.filter(r => r.state === 'overdue').forEach(r => {
    const b = out.find(x => r.overdueDays <= x.max)!;
    b.amount += r.left;
    b.count += 1;
  });
  return out;
}

// ---------------------------------------------------------------------------
// Quy định tiền theo gói

export type PriceSource = 'list' | 'branch' | 'promo';

/** Giá áp dụng hôm nay tại chi nhánh: giá chi nhánh (nếu có) → khuyến mãi đang chạy (lấy mức thấp hơn) */
export function effectivePrice(pkg: Pick<TreatmentPackage, 'price' | 'pricing'>, branchId: string, now = new Date()): { price: number; base: number; source: PriceSource; promoLabel?: string } {
  const pr = pricingOf(pkg);
  const branch = pr.branchPrices[branchId];
  const base = branch && branch > 0 ? branch : pkg.price;
  const today = ymd(now);
  if (pr.promo && pr.promo.price > 0 && pr.promo.from <= today && today <= pr.promo.to && pr.promo.price < base) {
    return { price: pr.promo.price, base, source: 'promo', promoLabel: pr.promo.label };
  }
  return { price: base, base, source: branch && branch > 0 ? 'branch' : 'list' };
}

/** Giá đưa vào đơn khi bán: giá áp dụng + phụ thu trả góp nếu trả từ 2 đợt (làm tròn nghìn) */
export function saleListPrice(pkg: Pick<TreatmentPackage, 'price' | 'pricing'>, branchId: string, installments: number, now = new Date()) {
  const eff = effectivePrice(pkg, branchId, now);
  const pct = installments > 1 ? pricingOf(pkg).installmentSurchargePct : 0;
  const surcharge = round1000(eff.price * pct / 100);
  return { ...eff, surchargePct: pct, surcharge, listPrice: eff.price + surcharge };
}

/** Chiết khấu tay tối đa: mức thấp hơn giữa hạn mức vai trò và quy định của gói */
export const discountCap = (pkg: Pick<TreatmentPackage, 'pricing'>, roleLimit: number) => Math.min(roleLimit, pricingOf(pkg).maxDiscountPct);

/** Quy định tiền áp cho thẻ: bản chụp lúc bán, thẻ cũ chưa có thì lấy theo gói hiện tại */
export const cardPolicy = (c: Pick<TreatmentCard, 'policy'>, pkg?: Pick<TreatmentPackage, 'pricing'>): CardPolicy => c.policy || policyOf(pkg);

/** Gia hạn thêm N tháng: phí và số tháng còn được gia hạn */
export function extensionQuote(c: Pick<TreatmentCard, 'policy' | 'extendedMonths'>, months: number, pkg?: Pick<TreatmentPackage, 'pricing'>) {
  const pol = cardPolicy(c, pkg);
  const used = c.extendedMonths || 0;
  const left = Math.max(0, pol.maxExtensionMonths - used);
  return { fee: Math.max(0, months) * pol.extensionFeePerMonth, used, left, allowed: months > 0 && months <= left };
}

/** Số tháng gia hạn (làm tròn lên) khi dời hạn từ ngày cũ sang ngày mới */
export const monthsBetween = (from: string, to: string) => Math.max(0, Math.ceil(dayjs(to).diff(dayjs(from), 'month', true) - 1e-9));

// ---------------------------------------------------------------------------
// Phiếu thu: một lần đóng tiền trả cho những đợt nào (tính theo thời điểm thu — in lại vẫn đúng)

export interface PaymentAllocation {
  payment: TreatmentCard['payments'][number];
  items: { no: number; dueDate: string; amount: number; applied: number; leftAfter: number }[];
  /** Phần trả trước vượt lịch (nếu có) */
  excess: number;
  paidAfter: number;
  debtAfter: number;
  /** Đợt kế tiếp còn thiếu sau lần thu này */
  next: { no: number; dueDate: string; left: number } | null;
}

export function paymentAllocation(c: TreatmentCard, paymentId: string): PaymentAllocation | null {
  const sorted = [...c.payments].sort((a, b) => a.at.localeCompare(b.at) || a.id.localeCompare(b.id));
  const idx = sorted.findIndex(p => p.id === paymentId);
  if (idx < 0) return null;
  let before = sum(sorted.slice(0, idx).map(p => p.amount));
  let amt = sorted[idx].amount;
  const items: PaymentAllocation['items'] = [];
  const inst = [...c.installments].sort((a, b) => a.no - b.no);
  inst.forEach(i => {
    const coveredBefore = Math.min(i.amount, Math.max(0, before));
    before -= coveredBefore;
    const need = i.amount - coveredBefore;
    if (need <= 0 || amt <= 0) return;
    const take = Math.min(need, amt);
    amt -= take;
    items.push({ no: i.no, dueDate: i.dueDate, amount: i.amount, applied: take, leftAfter: need - take });
  });
  const paidAfter = sum(sorted.slice(0, idx + 1).map(p => p.amount));
  let pool = paidAfter;
  let next: PaymentAllocation['next'] = null;
  for (const i of inst) {
    const covered = Math.min(i.amount, Math.max(0, pool));
    pool -= covered;
    if (covered < i.amount) { next = { no: i.no, dueDate: i.dueDate, left: i.amount - covered }; break; }
  }
  return { payment: sorted[idx], items, excess: amt, paidAfter, debtAfter: Math.max(0, c.price - paidAfter), next };
}
